import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  Avatar,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Assignment as AssignIcon,
  AssignmentReturn as ReleaseIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AIIcon,
  ThumbUp as OverrideApproveIcon,
  ThumbDown as OverrideRejectIcon,
} from '@mui/icons-material';
import moderationService from '../../services/moderationService';
import CustomPagination from '../../components/common/CustomPagination';

const ProductModeration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state) => state.auth);
  const [moderations, setModerations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [assignmentFilter, setAssignmentFilter] = useState('available'); // available, assigned_to_me, assigned_to_others
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  
  // Dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedModeration, setSelectedModeration] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  
  // AI Override states
  const [overrideDialog, setOverrideDialog] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideDecision, setOverrideDecision] = useState('');
  
  // Alert states
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  useEffect(() => {
    // Auto-release expired locks when page loads
    const autoReleaseExpired = async () => {
      try {
        await moderationService.releaseExpiredLocks();
        console.log('Auto-released expired locks on page load');
      } catch (error) {
        console.error('Error auto-releasing expired locks:', error);
      }
    };
    
    autoReleaseExpired();
    fetchModerations();
    fetchStats();
  }, [statusFilter, assignmentFilter, searchQuery, page, rowsPerPage]);

  const fetchModerations = async () => {
    try {
      setLoading(true);
      const params = {
        status: statusFilter === 'all' ? undefined : statusFilter,
        assignment: assignmentFilter === 'all' ? undefined : assignmentFilter,
        search: searchQuery || undefined,
        page: page + 1,
        page_size: rowsPerPage,
      };
      
      const response = await moderationService.getModerations(params);
      setModerations(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching moderations:', error);
      showAlert('Failed to load moderations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await moderationService.getStats();
      setStats(response.stats || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleApprove = async (moderation) => {
    try {
      await moderationService.approveProduct(moderation.id);
      showAlert('Product approved successfully', 'success');
      fetchModerations();
      fetchStats();
    } catch (error) {
      console.error('Error approving product:', error);
      showAlert('Failed to approve product', 'error');
    }
  };

  const handleRejectClick = (moderation) => {
    setSelectedModeration(moderation);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      showAlert('Please provide a rejection reason', 'warning');
      return;
    }

    try {
      await moderationService.rejectProduct(selectedModeration.id, rejectionReason);
      showAlert('Product rejected successfully', 'success');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchModerations();
      fetchStats();
    } catch (error) {
      console.error('Error rejecting product:', error);
      showAlert('Failed to reject product', 'error');
    }
  };

  const handleViewDetail = async (moderation) => {
    try {
      const detail = await moderationService.getModeration(moderation.id);
      setSelectedDetail(detail);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error fetching moderation detail:', error);
      showAlert('Failed to load product details', 'error');
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 5000);
  };

  // Assignment handlers
  const handleAssignToMe = async (moderation) => {
    try {
      await moderationService.assignToMe(moderation.id);
      showAlert('Product assigned to you successfully', 'success');
      fetchModerations();
    } catch (error) {
      console.error('Error assigning product:', error);
      if (error.response?.status === 409) {
        showAlert(`Product is already assigned to ${error.response.data.assigned_to}`, 'warning');
      } else {
        showAlert('Failed to assign product', 'error');
      }
    }
  };

  const handleReleaseAssignment = async (moderation) => {
    try {
      await moderationService.releaseAssignment(moderation.id);
      showAlert('Assignment released successfully', 'success');
      fetchModerations();
    } catch (error) {
      console.error('Error releasing assignment:', error);
      showAlert('Failed to release assignment', 'error');
    }
  };

  const handleOverrideClick = (moderation, decision) => {
    setSelectedModeration(moderation);
    setOverrideDecision(decision);
    setOverrideReason('');
    setOverrideDialog(true);
  };

  const handleOverrideConfirm = async () => {
    if (!overrideReason.trim()) {
      showAlert('Please provide a reason for the override', 'warning');
      return;
    }

    try {
      await moderationService.overrideAIDecision(
        selectedModeration.id, 
        overrideDecision, 
        overrideReason
      );
      showAlert(`AI decision overridden successfully - Product ${overrideDecision}d`, 'success');
      setOverrideDialog(false);
      setOverrideReason('');
      setOverrideDecision('');
      fetchModerations();
      fetchStats();
    } catch (error) {
      console.error('Error overriding AI decision:', error);
      showAlert('Failed to override AI decision', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 90) return '#4caf50';
    if (score >= 70) return '#ff9800';
    return '#f44336';
  };

  const getAssignmentStatus = (moderation) => {
    if (!moderation.assigned_to) return 'available';
    if (moderation.assignment_status === 'expired') return 'expired';
    return 'assigned';
  };

  const getAssignmentColor = (status) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'assigned':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatLockTime = (lockedUntil) => {
    if (!lockedUntil) return null;
    const now = new Date();
    const lockTime = new Date(lockedUntil);
    const diffMinutes = Math.floor((lockTime - now) / (1000 * 60));
    
    if (diffMinutes <= 0) return 'Expired';
    if (diffMinutes < 60) return `${diffMinutes}m`;
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(String(rowsPerPage));
      return;
    }
    setRowsPerPage(parseInt(value, 10));
    setCustomRowsActive(false);
    setPage(0);
  };

  const handleCustomRowsInputChange = (event) => {
    setCustomRowsInput(event.target.value);
  };

  const handleCustomRowsInputBlur = () => {
    const parsedValue = parseInt(customRowsInput, 10);
    if (Number.isNaN(parsedValue) || parsedValue <= 0) {
      setCustomRowsInput(String(rowsPerPage));
      return;
    }
    setRowsPerPage(parsedValue);
    setCustomRowsActive(false);
    setPage(0);
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const columns = [
    {
      field: 'product',
      headerName: 'Product',
      minWidth: 280,
      flex: 1.4,
      sortable: false,
      renderCell: ({ row: moderation }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Avatar
            src={moderation.product?.main_image}
            alt={moderation.product?.title}
            variant="rounded"
            sx={{ width: 48, height: 48, flexShrink: 0 }}
          >
            {moderation.product?.title?.charAt(0)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight="bold" noWrap>
              {moderation.product?.title || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {moderation.product?.sid || 'N/A'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'seller',
      headerName: 'Seller',
      minWidth: 160,
      flex: 0.8,
      valueGetter: (value, row) => row.product?.seller_name || 'N/A',
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      flex: 0.6,
      renderCell: ({ row: moderation }) => (
        <Chip
          label={moderation.status_display || moderation.status}
          color={getStatusColor(moderation.status)}
          size="small"
        />
      ),
    },
    {
      field: 'assignment',
      headerName: 'Assignment',
      minWidth: 190,
      flex: 0.9,
      sortable: false,
      renderCell: ({ row: moderation }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {moderation.assigned_to ? (
            <>
              <Chip
                label={`${moderation.assigned_to.fullname}`}
                color={getAssignmentColor(getAssignmentStatus(moderation))}
                size="small"
                icon={moderation.assignment_status === 'expired' ? <UnlockIcon /> : <LockIcon />}
              />
              {moderation.locked_until && (
                <Typography variant="caption" color="text.secondary">
                  {formatLockTime(moderation.locked_until)}
                </Typography>
              )}
            </>
          ) : (
            <Chip label="Available" color="success" size="small" icon={<UnlockIcon />} />
          )}
        </Box>
      ),
    },
    {
      field: 'ai_recommendation',
      headerName: 'AI Recommendation',
      minWidth: 210,
      flex: 1,
      sortable: false,
      renderCell: ({ row: moderation }) => (
        moderation.ai_recommendation ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Chip
              label={moderation.ai_recommendation_display || moderation.ai_recommendation}
              color={
                moderation.ai_recommendation === 'approve' ? 'success' :
                moderation.ai_recommendation === 'reject' ? 'error' : 'warning'
              }
              size="small"
              icon={<AIIcon />}
            />
            {moderation.ai_confidence && (
              <Typography variant="caption" color="text.secondary">
                Confidence: {(moderation.ai_confidence * 100).toFixed(1)}%
              </Typography>
            )}
            {moderation.admin_override && (
              <Chip label="Admin Override" color="info" size="small" variant="outlined" />
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">N/A</Typography>
        )
      ),
    },
    {
      field: 'confidence',
      headerName: 'Confidence',
      minWidth: 150,
      flex: 0.7,
      sortable: false,
      renderCell: ({ row: moderation }) => (
        moderation.confidence_score !== null ? (
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={moderation.confidence_percentage || 0}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getConfidenceColor(moderation.confidence_percentage || 0),
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {moderation.confidence_percentage || 0}%
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">N/A</Typography>
        )
      ),
    },
    {
      field: 'flags',
      headerName: 'Flags',
      minWidth: 120,
      flex: 0.5,
      renderCell: ({ row: moderation }) => (
        moderation.flags_count > 0 ? (
          <Chip
            label={`${moderation.flags_count} flag${moderation.flags_count > 1 ? 's' : ''}`}
            size="small"
            color="error"
            variant="outlined"
          />
        ) : (
          <Typography variant="caption" color="text.secondary">None</Typography>
        )
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      minWidth: 120,
      flex: 0.5,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 230,
      flex: 1,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row: moderation }) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap', width: '100%' }}>
          {moderation.status === 'pending' && (
            <>
              {!moderation.assigned_to ? (
                <Tooltip title="Assign to Me">
                  <IconButton size="small" color="primary" onClick={() => handleAssignToMe(moderation)}>
                    <AssignIcon />
                  </IconButton>
                </Tooltip>
              ) : moderation.assigned_to?.id === user?.id ? (
                <Tooltip title="Release Assignment">
                  <IconButton size="small" color="warning" onClick={() => handleReleaseAssignment(moderation)}>
                    <ReleaseIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Approve">
                <IconButton size="small" color="success" onClick={() => handleApprove(moderation)}>
                  <ApproveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton size="small" color="error" onClick={() => handleRejectClick(moderation)}>
                  <RejectIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          {moderation.status === 'pending' && moderation.ai_recommendation &&
            moderation.ai_recommendation !== 'review' && !moderation.admin_override && (
              <>
                {moderation.ai_recommendation === 'approve' && (
                  <Tooltip title="Override AI: Reject">
                    <IconButton size="small" color="error" onClick={() => handleOverrideClick(moderation, 'reject')}>
                      <OverrideRejectIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {moderation.ai_recommendation === 'reject' && (
                  <Tooltip title="Override AI: Approve">
                    <IconButton size="small" color="success" onClick={() => handleOverrideClick(moderation, 'approve')}>
                      <OverrideApproveIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          <Tooltip title="View Details">
            <IconButton size="small" color="info" onClick={() => handleViewDetail(moderation)}>
              <ViewIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          gutterBottom
          sx={{ mb: { xs: 2, sm: 3 }, color: theme.palette.primary.main, fontWeight: 'bold' }}
        >
          Product Moderation
        </Typography>

      {/* Alert */}
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid size={{ xs: 6, sm: 6, md: 2 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Total
              </Typography>
              <Typography variant={isMobile ? "h5" : "h4"}>{stats.total || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.pending || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Requires Review
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.requires_review || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2}}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Approved
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.approved || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{xs: 6, sm: 6, md: 2 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Rejected
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.rejected || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Assignment Filter Tabs */}
      <Paper sx={{ p: { xs: 1, sm: 1.5 }, mb: { xs: 1.5, sm: 2 } }}>
        <Grid container spacing={{ xs: 1, sm: 1.5 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Button
              fullWidth
              variant={assignmentFilter === 'available' ? 'contained' : 'outlined'}
              size={isMobile ? "small" : "medium"}
              onClick={() => {
                setAssignmentFilter('available');
                setPage(0);
              }}
              startIcon={!isMobile && <UnlockIcon />}
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 0.5, sm: 2 }
              }}
            >
              {isMobile ? `Avail. (${stats.available_count || 0})` : `Available (${stats.available_count || 0})`}
            </Button>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Button
              fullWidth
              variant={assignmentFilter === 'assigned_to_me' ? 'contained' : 'outlined'}
              size={isMobile ? "small" : "medium"}
              color="warning"
              onClick={() => {
                setAssignmentFilter('assigned_to_me');
                setPage(0);
              }}
              startIcon={!isMobile && <PersonIcon />}
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 0.5, sm: 2 }
              }}
            >
              {isMobile ? `Mine (${stats.my_assignments_count || 0})` : `My Assignments (${stats.my_assignments_count || 0})`}
            </Button>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Button
              fullWidth
              variant={assignmentFilter === 'assigned_to_others' ? 'contained' : 'outlined'}
              size={isMobile ? "small" : "medium"}
              color="info"
              onClick={() => {
                setAssignmentFilter('assigned_to_others');
                setPage(0);
              }}
              startIcon={!isMobile && <LockIcon />}
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 0.5, sm: 2 }
              }}
            >
              {isMobile ? `Others (${stats.others_assignments_count || 0})` : `Others' Assignments (${stats.others_assignments_count || 0})`}
            </Button>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Button
              fullWidth
              variant={assignmentFilter === 'all' ? 'contained' : 'outlined'}
              size={isMobile ? "small" : "medium"}
              onClick={() => {
                setAssignmentFilter('all');
                setPage(0);
              }}
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                px: { xs: 0.5, sm: 2 }
              }}
            >
              All
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by product title, or seller..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchModerations();
                fetchStats();
              }}
            >
              Refresh
            </Button>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              startIcon={<UnlockIcon />}
              onClick={async () => {
                try {
                  await moderationService.releaseExpiredLocks();
                  showAlert('Expired locks released successfully', 'success');
                  fetchModerations();
                  fetchStats();
                } catch (error) {
                  console.error('Error releasing expired locks:', error);
                  showAlert('Failed to release expired locks', 'error');
                }
              }}
            >
              Release Expired
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <DataGrid
              autoHeight
              rows={moderations}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooter
              getRowHeight={() => 'auto'}
              sx={{
                border: 'none',
                minWidth: 1180,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: theme.palette.primary.contrastText,
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton': {
                  color: theme.palette.primary.contrastText,
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '& .MuiDataGrid-cell': {
                  py: 1,
                  alignItems: 'center',
                },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
      {!loading && (
        <CustomPagination
          page={page}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Product</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this product. The merchant will receive this feedback.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="e.g., Images are too low quality, description is incomplete..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRejectConfirm} variant="contained" color="error">
            Reject Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Product Details</DialogTitle>
        <DialogContent>
          {selectedDetail && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6">{selectedDetail.product?.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    SID: {selectedDetail.product?.sid}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" fontWeight="bold">Seller:</Typography>
                  <Typography variant="body2">{selectedDetail.product?.seller_name}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" fontWeight="bold">Category:</Typography>
                  <Typography variant="body2">{selectedDetail.product?.category_name}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" fontWeight="bold">Status:</Typography>
                  <Chip
                    label={selectedDetail.status_display}
                    color={getStatusColor(selectedDetail.status)}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" fontWeight="bold">Confidence:</Typography>
                  <Typography variant="body2">{selectedDetail.confidence_percentage}%</Typography>
                </Grid>
                {selectedDetail.auto_flag && selectedDetail.auto_flag.flags && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" fontWeight="bold">Auto-Detection Flags:</Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedDetail.auto_flag.flags.map((flag, index) => (
                        <Chip key={index} label={flag} size="small" color="error" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}
                {selectedDetail.reason && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" fontWeight="bold">Reason:</Typography>
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.100' }}>
                      <Typography variant="body2">{selectedDetail.reason}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* AI Override Dialog */}
      <Dialog open={overrideDialog} onClose={() => setOverrideDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Override AI Decision
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The AI recommends <strong>{selectedModeration?.ai_recommendation}</strong> with{' '}
            {selectedModeration?.ai_confidence ? `${(selectedModeration.ai_confidence * 100).toFixed(1)}%` : 'unknown'} confidence.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You are about to <strong>{overrideDecision}</strong> this product, overriding the AI recommendation.
            Please provide a reason for this override to help improve the AI model.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Override Reason"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="Explain why you disagree with the AI recommendation..."
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleOverrideConfirm} 
            variant="contained"
            color={overrideDecision === 'approve' ? 'success' : 'error'}
            startIcon={overrideDecision === 'approve' ? <OverrideApproveIcon /> : <OverrideRejectIcon />}
          >
            Override & {overrideDecision === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductModeration;

